/**
 * @fileoverview API Service Layer
 *
 * Centralized API service for all backend communication.
 * This module provides a clean interface for making HTTP requests
 * and handles common concerns like error handling, retries, and timeouts.
 *
 * @module services/api
 */

import config, { buildApiUrl } from '../config';

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  status: number;
  data: unknown;
  constructor(message: string, status: number, data: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const fetchWithRetry = async (
  url: string,
  options: RequestInit = {},
  retries: number = config.api.retryAttempts
): Promise<Response> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.api.timeout);

    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response;
  } catch (error: unknown) {
    if (retries > 0 && error instanceof Error && error.name !== 'AbortError') {
      await sleep(config.api.retryDelay);
      return fetchWithRetry(url, options, retries - 1);
    }
    throw error;
  }
};

const parseResponse = async (response: Response): Promise<unknown> => {
  const contentType = response.headers.get('content-type');

  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  return response.text();
};

const request = async (endpoint: string, options: RequestInit = {}): Promise<unknown> => {
  const url = buildApiUrl(endpoint);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = config.dashboardToken;
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const defaultOptions: RequestInit = { headers };

  const mergedOptions: RequestInit = {
    ...defaultOptions,
    ...options,
    headers: {
      ...(defaultOptions.headers as Record<string, string>),
      ...(options.headers as Record<string, string>),
    },
  };

  const response = await fetchWithRetry(url, mergedOptions);
  const data = await parseResponse(response);

  if (!response.ok) {
    const errPayload = (data as { error?: string; message?: string })?.error
      ?? (data as { message?: string })?.message
      ?? `HTTP ${response.status}`;
    const message =
      typeof errPayload === 'string'
        ? errPayload
        : (errPayload as { message?: string })?.message ?? JSON.stringify(errPayload);
    throw new ApiError(message, response.status, data);
  }

  return data;
};

/**
 * API Service object with all available methods
 */
const api = {
  getMeetingPresets: async (): Promise<unknown[]> => {
    const response = (await request(config.endpoints.meetingPresets())) as { data?: { presets?: unknown[] }; presets?: unknown[] };
    const data = response?.data ?? response;
    return (data as { presets?: unknown[] })?.presets ?? [];
  },

  getMeetingState: async (
    meetingId: string,
    options?: { viewerTimezone?: string | null },
  ): Promise<unknown> => {
    const tz =
      options?.viewerTimezone ??
      (typeof Intl !== "undefined"
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null);
    const qs =
      tz && String(tz).trim()
        ? `?viewerTz=${encodeURIComponent(String(tz).trim())}`
        : "";
    return request(config.endpoints.zoomState(meetingId) + qs);
  },

  updateMeetingState: async (meetingId: string, data: unknown): Promise<unknown> => {
    return request(config.endpoints.zoomState(meetingId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getBotStatus: async (meetingUUID: string): Promise<unknown> => {
    return request(config.endpoints.botStatus(meetingUUID));
  },

  createBot: async (data: unknown): Promise<unknown> => {
    return request(config.endpoints.createBot, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  initAgendaEnforcer: async (meetingId: string, data: unknown): Promise<unknown> => {
    return request(config.endpoints.agendaInit(meetingId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getAgendaStatus: async (meetingId: string): Promise<unknown> => {
    return request(config.endpoints.agendaStatus(meetingId));
  },

  setAgendaItems: async (meetingId: string, items: unknown[]): Promise<unknown> => {
    return request(config.endpoints.agendaItems(meetingId), {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  },

  startAgenda: async (meetingId: string): Promise<unknown> => {
    return request(config.endpoints.agendaStart(meetingId), {
      method: 'POST',
    });
  },

  advanceAgenda: async (meetingId: string, force: boolean = false): Promise<unknown> => {
    return request(config.endpoints.agendaAdvance(meetingId), {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  },

  skipAgendaItem: async (meetingId: string, force: boolean = true): Promise<unknown> => {
    return request(config.endpoints.agendaSkip(meetingId), {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  },

  recordDecision: async (meetingId: string, text: string): Promise<unknown> => {
    return request(config.endpoints.agendaDecision(meetingId), {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  },

  resetAgenda: async (meetingId: string): Promise<unknown> => {
    return request(config.endpoints.agendaReset(meetingId), {
      method: 'POST',
    });
  },

  initInterruptModel: async (meetingId: string, data: unknown): Promise<unknown> => {
    return request(config.endpoints.interruptInit(meetingId), {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getInterruptStatus: async (meetingId: string): Promise<unknown> => {
    return request(config.endpoints.interruptStatus(meetingId));
  },

  toggleInterruptModel: async (meetingId: string): Promise<unknown> => {
    return request(config.endpoints.interruptToggle(meetingId), {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },

  setInterruptStrictness: async (meetingId: string, level: string): Promise<unknown> => {
    return request(config.endpoints.interruptStrictness(meetingId), {
      method: 'POST',
      body: JSON.stringify({ level }),
    });
  },

  resetAllInterrupts: async (meetingId: string): Promise<unknown> => {
    return request(config.endpoints.interruptReset(meetingId), {
      method: 'POST',
    });
  },

  resetParticipantInterrupts: async (meetingId: string, participantId: string): Promise<unknown> => {
    return request(config.endpoints.interruptResetParticipant(meetingId, participantId), {
      method: 'POST',
    });
  },

  generateTTS: async (text: string, voice: string = 'alloy'): Promise<unknown> => {
    return request(config.endpoints.ttsIntro, {
      method: 'POST',
      body: JSON.stringify({ text, voice }),
    });
  },

  getLiveInsights: async (meetingId: string): Promise<unknown> => {
    return request(`/api/meetings/${encodeURIComponent(meetingId)}/live-insights`);
  },

  postSharedAction: async (
    meetingId: string,
    body: { signalId: string; action: string },
  ): Promise<unknown> => {
    return request(`/api/meetings/${encodeURIComponent(meetingId)}/live-insights/shared-actions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  postPersonalAction: async (
    meetingId: string,
    body: { itemId: string; action: string },
  ): Promise<unknown> => {
    return request(`/api/meetings/${encodeURIComponent(meetingId)}/live-insights/personal-actions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  sendCommand: async (
    meetingId: string,
    command: string,
  ): Promise<{ emitted: boolean; intent: string | null }> => {
    return request(config.endpoints.agendaCommand(meetingId), {
      method: 'POST',
      body: JSON.stringify({ command }),
    }) as Promise<{ emitted: boolean; intent: string | null }>;
  },
};

export default api;

// ============================================================================
// Screenshare content sharing — standalone exported functions
// ============================================================================

/**
 * Update (upsert) the live document state for a meeting.
 * Requires the host bearer token.
 */
export async function updateDocumentState(
  meetingId: string,
  payload: { attachment_id?: string | null; page_number: number; display_mode: 'agenda' | 'content'; is_manual_override?: boolean },
  token: string,
): Promise<void> {
  const url = buildApiUrl(`/api/meetings/${encodeURIComponent(meetingId)}/document-state`);
  const response = await fetchWithRetry(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const data = await parseResponse(response);
    const errPayload = (data as { error?: string; message?: string })?.error
      ?? (data as { message?: string })?.message
      ?? `HTTP ${response.status}`;
    throw new ApiError(typeof errPayload === 'string' ? errPayload : JSON.stringify(errPayload), response.status, data);
  }
}

/**
 * Fetch the list of meeting attachments for the given meeting.
 * Requires the host bearer token.
 */
export async function fetchMeetingAttachments(
  meetingId: string,
  token: string,
): Promise<Array<{ id: string; filename: string; processing_status: string; page_count: number | null; content_type: string; screenshare_status: string }>> {
  const url = buildApiUrl(`/api/meetings/${encodeURIComponent(meetingId)}/attachments`);
  const response = await fetchWithRetry(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const data = await parseResponse(response);
    const errPayload = (data as { error?: string; message?: string })?.error
      ?? (data as { message?: string })?.message
      ?? `HTTP ${response.status}`;
    throw new ApiError(typeof errPayload === 'string' ? errPayload : JSON.stringify(errPayload), response.status, data);
  }
  const json = await response.json() as { data?: { attachments?: unknown[] } | unknown[] };
  const inner = json?.data;
  const items = (Array.isArray(inner) ? inner : (inner as Record<string, unknown>)?.attachments ?? []) as Record<string, unknown>[];
  return items.map((item) => ({
    id: String(item.id ?? ''),
    filename: String(item.filename ?? ''),
    processing_status: String(item.processingStatus ?? item.processing_status ?? ''),
    page_count: item.pageCount != null ? Number(item.pageCount) : (item.page_count != null ? Number(item.page_count) : null),
    content_type: String(item.contentType ?? item.content_type ?? ''),
    screenshare_status: String(item.screenshareStatus ?? item.screenshare_status ?? 'not_decided'),
  }));
}
